import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '@medical/shared/contexts/AuthContext';
import { hasFeature } from '@medical/shared/utils/features';
import { MedicationReminderProvider } from './contexts/MedicationReminderContext';
import Navbar from './components/Navbar';
import FloatingAboutButton from '@medical/shared/components/FloatingAboutButton';
import FloatingNotificationButton from './components/FloatingNotificationButton';
import NotificationPermissionBanner from './components/NotificationPermissionBanner';
import { usePushNotifications } from './hooks/usePushNotifications';
import Dashboard from './pages/Dashboard';
import BodyDiagram from './pages/BodyDiagram';
import PainLogs from './pages/PainLogs';
import Statistics from './pages/Statistics';
import History from './pages/History';
import Distress from './pages/Distress';
import Profile from './pages/Profile';
import Messages from './pages/Messages';
import Medication from './pages/Medication';

const MainContent = styled.main`
  margin-left: 280px;
  min-height: 100vh;
  padding: 2rem;

  @media (max-width: 768px) {
    margin-left: 0;
    padding: 1rem;
  }
`;

const PatientApp = () => {
  const { user, token } = useAuth();

  const { isSupported, permission, subscribed, loading: pushLoading, subscribe } =
    usePushNotifications({ token, enabled: !!(user && user.role === 'patient') });

  const showBanner =
    isSupported && permission === 'default' && !subscribed;
  const showMessages = hasFeature(user, 'messaging') || hasFeature(user, 'medicationReminders');

  return (
    <MedicationReminderProvider token={token} user={user}>
      {showBanner && (
        <NotificationPermissionBanner
          onEnable={subscribe}
          loading={pushLoading}
        />
      )}
      <Navbar />
      <MainContent>
        <Routes>
          <Route path="/" element={<BodyDiagram />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/body-diagram" element={<BodyDiagram />} />
          <Route path="/pain-logs" element={<PainLogs />} />
          <Route path="/statistics" element={<Statistics />} />
          <Route path="/history" element={<History />} />
          {hasFeature(user, 'distressThermometer') && <Route path="/distress" element={<Distress />} />}
          <Route path="/profile" element={<Profile />} />
          {showMessages && <Route path="/messages" element={<Messages />} />}
          {hasFeature(user, 'medicationReminders') && <Route path="/medication" element={<Medication />} />}
          {showMessages && <Route path="/notifications" element={<Messages />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </MainContent>
      <FloatingAboutButton />
      <FloatingNotificationButton />
    </MedicationReminderProvider>
  );
};

export default PatientApp;
